import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Zap, Rocket } from 'lucide-react';

const PlansSection = () => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });

  const goals = [
    {
      title: 'Motos',
      description: 'Tu libertad en dos ruedas',
      image: 'https://images.unsplash.com/photo-1558981806-ec527fa84c39?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      gradient: 'from-[#fbb040] to-orange-500',
    },
    {
      title: 'Vehículos',
      description: 'Resguarda tu camino',
      image: 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      gradient: 'from-yellow-400 to-[#fbb040]',
    },
    {
      title: 'Ferretería',
      description: 'No es solo una casa, es tu hogar',
      image: 'https://images.unsplash.com/photo-1517581177682-a085bb7ffb15?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      gradient: 'from-[#fbb040] to-yellow-500',
    },
    {
      title: 'Educación',
      description: 'Planifica el progreso',
      image: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      gradient: 'from-yellow-500 to-orange-400',
    },
    {
      title: 'Navidades',
      description: 'Anticípate a las fiestas',
      image: 'https://images.unsplash.com/photo-1512389142860-9c449e58a543?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      gradient: 'from-[#fbb040] to-yellow-600',
    },
    {
      title: 'Salud',
      description: 'Bienestar y cuidado',
      image: 'https://images.unsplash.com/photo-1758691462493-120a069304e6?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      gradient: 'from-yellow-400 to-[#fbb040]',
    },
  ];

  return (
    <section id="planes" ref={ref} className="relative py-32 bg-black overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <motion.div
          animate={{ opacity: [0.1, 0.3, 0.1] }}
          transition={{ duration: 4, repeat: Infinity }}
          className="absolute inset-0 bg-gradient-to-br from-[#fbb040]/10 via-transparent to-yellow-500/10"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            animate={{ rotate: [0, 10] }}
            transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
            className="inline-flex items-center gap-4 mb-6"
          >
            <Zap size={64} className="text-[#fbb040]" fill="currentColor" />
            <Rocket size={64} className="text-yellow-400" />
          </motion.div>
          
          <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
            Elige la{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fbb040] via-yellow-300 to-[#fbb040] animate-gradient">
              META
            </span>
            {' '}que quieres lograr
          </h2>
          <p className="text-2xl text-gray-300 max-w-3xl mx-auto font-bold">
            Diferentes metas para diferentes momentos de la vida.{' '}
            <span className="text-[#fbb040]">Todas con el respaldo de Kompa.</span>
          </p>
        </motion.div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {goals.map((goal, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 100, scale: 0.5, rotateY: -90 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1, rotateY: 0 } : {}}
              transition={{ 
                duration: 0.8, 
                delay: index * 0.15,
                type: "spring",
                bounce: 0.4
              }}
              whileHover={{ 
                scale: 1.05, 
                y: -20,
                rotateY: 5,
              }}
              transition={{ duration: 0.3 }}
              className="group cursor-pointer h-full"
              style={{ perspective: 1000 }}
            >
              <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-3xl overflow-hidden shadow-2xl border-4 border-[#fbb040] hover:border-yellow-300 transition-all h-full flex flex-col">
                {/* Image with K logo overlay inside */}
                <div className="relative h-64 overflow-hidden">
                  <motion.img
                    whileHover={{ scale: 1.15 }}
                    transition={{ duration: 0.6 }}
                    src={goal.image}
                    alt={goal.title}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${goal.gradient} opacity-60 group-hover:opacity-80 transition-opacity`} />
                  
                  {/* K logo spinning overlay - INSIDE image */}
                  <motion.div
                    animate={{ 
                      rotate: 360,
                    }}
                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_f2ee6b04-88a4-4dce-acdb-9e470155c901/artifacts/8mgrjxkj_K%202.png"
                      alt="K"
                      className="w-24 h-24 opacity-40"
                    />
                  </motion.div>
                  
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-4xl font-black text-white drop-shadow-2xl">{goal.title}</h3>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-8 relative flex-grow flex items-center">
                  <p className="text-gray-300 text-lg font-semibold relative z-10">{goal.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PlansSection;